import MyContext from './AppContext';
import { NavigationView } from '@/shared/constants/navigation-view.enum';
import { navigationViewFromApiVisao } from '@/shared/navigation/navigation-view.utils';
import {
  getLoginTokenFromStorage,
  getUserIdFromLoginJwt,
  isLogedIn,
  isPublicAuthRoute,
  isPublicCandidateRoute,
  SetPermissoesFromRoleLevels,
  deveExibirNavbarLoginTokenInterno,
} from '@/modules/system/auth/application/auth.service';
import { GetAuthMe } from '@/modules/system/usuario/usuario.service';
import { CorregedoriaCheck } from '@/modules/reports/application/relatorios-corregedoria.service';
import { BuscarDefensoresVinculadosAssessor } from '@/modules/reports/application/relatorios-atividades.service';
import { ReactNode, useEffect, useState } from 'react';
import { UsuarioConfigs } from '../types/usuario_config';

interface MyProviderProps {
  children: ReactNode;
}

export const initialState: UsuarioConfigs = {
  username: '',
  cor_fonte_zebrado_tabela: '',
  cor_fundo_zebrado_tabela: '',
  nome: '',
  navbar: false,
  userId: undefined,
  navigationViewOverride: undefined,
};

export const MyProvider: React.FC<MyProviderProps> = ({ children }) => {
  const [state, setStateInternal] = useState<UsuarioConfigs>(() => {
    const base = UsuarioConfigs.GetConfigs() || initialState;
    if (base.userId == null) {
      const fromJwt = getUserIdFromLoginJwt();
      if (fromJwt != null) return { ...base, userId: fromJwt };
    }
    return base;
  });
  const [sessionReady, setSessionReady] = useState(() => !isLogedIn());
  const [authVisao, setAuthVisao] = useState<NavigationView | null>(null);
  const [ehDefensorDiretor, setEhDefensorDiretor] = useState(false);
  const [isCorregedoria, setIsCorregedoria] = useState(false);
  const [assessorPodeEditarRelatorio, setAssessorPodeEditarRelatorio] = useState(false);

  const setState: React.Dispatch<React.SetStateAction<UsuarioConfigs>> = (
    value,
  ) => {
    setStateInternal((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      UsuarioConfigs.SetConfigs(next);
      return next;
    });
  };

  useEffect(() => {
    const token = getLoginTokenFromStorage();

    if (!token) {
      setAuthVisao(null);
      setEhDefensorDiretor(false);
      setIsCorregedoria(false);
      setSessionReady(true);
      return;
    }

    // Portal do candidato: não carrega sessão interna (evita 401 e redirecionamento).
    if (isPublicCandidateRoute()) {
      setAuthVisao(null);
      setEhDefensorDiretor(false);
      setIsCorregedoria(false);
      setSessionReady(true);
      return;
    }

    // Na tela de login, antes de identificar o usuário, não bloqueia a UI pública.
    if (isPublicAuthRoute() && state.userId == null) {
      setAuthVisao(null);
      setEhDefensorDiretor(false);
      setIsCorregedoria(false);
      setSessionReady(true);
      return;
    }

    setSessionReady(false);

    let cancel = false;
    void GetAuthMe()
      .then(async (me) => {
        if (cancel || !me) return;
        setAuthVisao(navigationViewFromApiVisao(me.visao));
        setEhDefensorDiretor(Boolean(me.ehDefensorDiretor));
        try {
          setIsCorregedoria(await CorregedoriaCheck());
        } catch {
          setIsCorregedoria(false);
        }
        try {
          const cpf = me.cpf?.trim();
          if (cpf) {
            const vinculados = await BuscarDefensoresVinculadosAssessor(cpf);
            setAssessorPodeEditarRelatorio(vinculados.length > 0);
          } else {
            setAssessorPodeEditarRelatorio(false);
          }
        } catch {
          setAssessorPodeEditarRelatorio(false);
        }
        if (me.permissoes?.length) {
          SetPermissoesFromRoleLevels(me.permissoes);
        }
        const visaoApi = navigationViewFromApiVisao(me.visao);
        setState((prev) => {
          const next: UsuarioConfigs = {
            ...prev,
            userId: prev.userId ?? me.userId,
            nome: prev.nome?.trim() ? prev.nome : me.nome?.trim() || prev.nome,
            cpf: prev.cpf?.trim() ? prev.cpf : me.cpf?.trim() || prev.cpf,
            pessoaId: me.pessoaId ?? prev.pessoaId,
            username: prev.username?.trim()
              ? prev.username
              : me.username ?? prev.username,
            cor_fonte_zebrado_tabela:
              prev.cor_fonte_zebrado_tabela || me.corletratabela || '',
            cor_fundo_zebrado_tabela:
              prev.cor_fundo_zebrado_tabela || me.corfundotabela || '',
            navigationViewOverride: visaoApi ?? prev.navigationViewOverride,
            navbar:
              prev.navbar ||
              deveExibirNavbarLoginTokenInterno(me.visao, me.userId),
          };
          return next;
        });
      })
      .catch(() => {
        if (!cancel) {
          setAuthVisao(null);
          setEhDefensorDiretor(false);
          setIsCorregedoria(false);
        }
        /* sessão inválida: token removido em GetAuthMe quando 401 */
      })
      .finally(() => {
        if (!cancel) setSessionReady(true);
      });

    return () => {
      cancel = true;
    };
  }, [state.userId]);

  return (
    <MyContext.Provider
      value={{
        state,
        setState,
        sessionReady,
        authVisao,
        ehDefensorDiretor,
        isCorregedoria,
        assessorPodeEditarRelatorio,
      }}
    >
      {children}
    </MyContext.Provider>
  );
};
